import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import RateMarquee from "@/components/RateMarquee";
import Navbar from "@/components/Navbar";
import CashOnlyBanner from "@/components/CashOnlyBanner";
import Footer from "@/components/Footer";
import FAQWidget from "@/components/FAQWidget";
import AIPreviewPopup from "@/components/AIPreviewPopup";
import Seo from "@/components/Seo";
import Home from "@/pages/Home";
import About from "@/pages/About";
import FrontlineHeroes from "@/pages/FrontlineHeroes";
import Contact from "@/pages/Contact";
import Crypto from "@/pages/Crypto";
import CryptoDetail from "@/pages/CryptoDetail";
import CryptoNews from "@/pages/CryptoNews";
import CryptoAcademy from "@/pages/CryptoAcademy";
import AIPredictor from "@/pages/AIPredictor";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Layout component that conditionally renders navbar/footer
function Layout({ children }) {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAdmin = location.pathname.startsWith('/admin');
  const hideLayout = isDashboard || isAdmin;
  // No popup in the static snapshots made at build time (scripts/prerender.py).
  const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent.includes('FonderXPrerender');
  const showPopup = location.pathname === '/' && !isPrerender;

  return (
    <>
      <Seo />
      {showPopup && <AIPreviewPopup />}
      {!hideLayout && <RateMarquee />}
      {!hideLayout && <Navbar />}
      {!hideLayout && <CashOnlyBanner />}
      <main className="flex-1">
        {children}
      </main>
      {!hideLayout && <Footer />}
      {!hideLayout && <FAQWidget />}
    </>
  );
}

function AppContent() {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {}
    };
    seedData();
  }, []);

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/heroes" element={<FrontlineHeroes />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/crypto" element={<Crypto />} />
        <Route path="/crypto/news" element={<CryptoNews />} />
        <Route path="/crypto/academy" element={<CryptoAcademy />} />
        <Route path="/crypto/:coinId" element={<CryptoDetail />} />
        <Route path="/ai-predictor" element={<AIPredictor />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppContent />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
